import { useEffect, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { leggiRigheSenzaCosto, salvaCosti, type RigaCosto } from "../costiManualiApi";
import { importoSbagliato, leggiImportoEuro } from "@/shared/importo";

/** «12,5» o «12.5» → 12.5; vuoto, zero o testo → null. */
export function leggiEuro(testo: string): number | null {
  return leggiImportoEuro(testo);
}

/**
 * «Costo non noto: quanto lo paghiamo?» (Davide 03/11/2026). Compare solo se OM non sa
 * il costo di una riga dell'ordine. Un campo per SKU, € senza IVA per pezzo, un clic su
 * Salva: il margine dell'ordine si ricalcola subito. Il costo vale solo per la BI di
 * questo ordine e non cambia il PIM. Se la lettura fallisce non si vede niente: la
 * pagina e l'invio funzionano lo stesso.
 */
export function CostoNonNotoBox({
  riferimento,
  onSaved,
}: {
  riferimento: string;
  /** Dopo il salvataggio: chi mostra il margine lo rilegge. */
  onSaved?: () => void;
}) {
  const [righe, setRighe] = useState<RigaCosto[]>([]);
  const [testi, setTesti] = useState<Record<string, string>>({});
  const [occupato, setOccupato] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [fatto, setFatto] = useState(false);

  useEffect(() => {
    let vivo = true;
    setFatto(false);
    leggiRigheSenzaCosto(riferimento)
      .then((r) => vivo && setRighe(r?.righe ?? []))
      .catch(() => vivo && setRighe([]));
    return () => {
      vivo = false;
    };
  }, [riferimento]);

  const aperte = righe.filter((r) => r.costo_netto == null);
  const daScrivere = aperte.filter((r) => leggiEuro(testi[r.sku] ?? "") !== null);

  async function salva() {
    setOccupato(true);
    setErrore(null);
    try {
      const costi = daScrivere.map((r) => ({
        sku: r.sku,
        costo: leggiEuro(testi[r.sku] ?? "") as number,
      }));
      const dopo = await salvaCosti(riferimento, costi);
      setRighe(dopo?.righe ?? []);
      setTesti({});
      setFatto(true);
      onSaved?.();
    } catch (e) {
      setErrore(prettifyApiError(e));
    } finally {
      setOccupato(false);
    }
  }

  if (aperte.length === 0) {
    if (!fatto) return null;
    return (
      <p
        role="status"
        className="mb-4 rounded-lg border border-green/30 bg-green-soft px-4 py-2.5 text-[13px] text-ink"
      >
        Costo salvato. Il margine è ricalcolato.
      </p>
    );
  }

  return (
    <section
      aria-labelledby={`costo-noto-${riferimento}`}
      className="mb-4 rounded-xl border border-amber-500/50 bg-amber-50 px-4 py-3.5"
    >
      <h3 id={`costo-noto-${riferimento}`} className="text-[15px] font-bold leading-tight text-ink">
        Costo non noto: quanto lo paghiamo?
      </h3>
      <p className="mb-3 mt-1 text-[13px] text-ink">
        Senza il costo il margine non si calcola. Scrivi il prezzo per un pezzo, senza IVA.
      </p>
      <ul className="space-y-2">
        {aperte.map((r) => (
          <li key={r.sku} className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="min-w-0 flex-1 text-[13px] text-ink">
              <span className="font-mono">{r.sku}</span>
              {r.titolo ? ` — ${r.titolo}` : ""} × {r.quantita}
            </span>
            <label className="flex items-center gap-1.5 text-[13px] text-ink">
              €
              <input
                type="text"
                inputMode="decimal"
                aria-label={`Costo di ${r.sku}, euro senza IVA`}
                placeholder="0,00"
                value={testi[r.sku] ?? ""}
                disabled={occupato}
                onChange={(e) => setTesti((t) => ({ ...t, [r.sku]: e.target.value }))}
                aria-invalid={importoSbagliato(testi[r.sku] ?? "") || undefined}
                className={`h-9 w-24 rounded-sm border bg-card px-2 text-right tabular-nums disabled:opacity-50 max-md:h-11 ${importoSbagliato(testi[r.sku] ?? "") ? "border-red" : "border-line"}`}
              />
              <span className="text-[12px] text-ink-2">senza IVA</span>
            </label>
            {importoSbagliato(testi[r.sku] ?? "") && (
              <span role="status" className="w-full text-[12px] font-semibold text-red">
                Importo non valido: scrivi per esempio 12,50
              </span>
            )}
          </li>
        ))}
      </ul>
      <button
        type="button"
        disabled={occupato || daScrivere.length === 0}
        onClick={() => void salva()}
        className="mt-3 min-h-9 rounded-md bg-ink px-3.5 py-1.5 text-[13px] font-semibold text-paper disabled:opacity-40 max-md:min-h-11"
      >
        Salva
      </button>
      {errore && (
        <p role="alert" className="mt-2 text-[12px] text-red">
          {errore}
        </p>
      )}
    </section>
  );
}
